<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs } from 'vue';
import { recordIdFromResponse } from './imudeiResponse';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  screen: { type: String, required: true },
});

const emit = defineEmits(['navigate']);
const realData = inject('imudeiRealData');
const { error, loading, refresh, saving } = realData;
const model = proxyRefs(
  useImudeiRuntimeModel({
    realData,
    module: computed(() => 'sales'),
    screen: computed(() => props.screen),
  })
);

const valueAt = (source, path) =>
  path.split('.').reduce((value, key) => value?.[key], source);

const firstValue = (source, paths, fallback = '') =>
  paths.map(path => valueAt(source, path)).find(Boolean) ?? fallback;

const asArray = value => (Array.isArray(value) ? value : []);

const shortDate = value => {
  if (!value) return 'Não informado';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'medium' }).format(date);
};

const money = value => {
  if (value == null || value === '') return 'Não informado';
  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency',
  }).format(amount);
};

const initials = name =>
  String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const source = computed(() => model.detail?.source || {});
const isCreate = computed(() => props.screen === 'create');
const isEdit = computed(() => props.screen === 'edit');
const isDetail = computed(() => props.screen === 'detail');
const salesStages = Object.freeze([
  ['Cadastro', 'Rascunho'],
  ['Documentos', 'Revisão'],
  ['Assinatura', 'Entrega'],
  ['Pós-venda', 'Finalização'],
]);
const stageIndex = computed(() => {
  const stage = String(
    firstValue(source.value, ['sales_stage', 'status'], '')
  ).toLocaleLowerCase('pt-BR');
  if (stage.includes('final')) return 3;
  if (stage.includes('entreg') || stage.includes('assin')) return 2;
  if (stage.includes('revis')) return 1;
  return 0;
});
const propertyImage = computed(() =>
  firstValue(source.value, [
    'property.cover_image_url',
    'property.image_url',
    'property.image',
    'imovel.cover_image_url',
    'imovel.image_url',
  ])
);
const propertyAddress = computed(() =>
  firstValue(source.value, [
    'address.endereco',
    'address.logradouro',
    'property.address',
    'imovel.endereco',
  ])
);
const propertyLocation = computed(() =>
  [
    firstValue(source.value, ['address.bairro', 'imovel.bairro']),
    firstValue(source.value, ['address.cidade', 'imovel.cidade']),
    firstValue(source.value, ['address.uf', 'imovel.uf']),
  ]
    .filter(Boolean)
    .join(' · ')
);
const manager = computed(() =>
  firstValue(source.value, [
    'manager_details.name',
    'manager.name',
    'post_sale.responsible.name',
  ])
);
const statusLabel = computed(() =>
  firstValue(source.value, ['sales_stage_label', 'status'], 'Sem status')
);
const participants = computed(() => {
  const partyRecords = asArray(
    source.value.party_details ||
      source.value.participants ||
      source.value.parties
  );
  const fixedPeople = [
    [source.value.buyer, 'Comprador(a)'],
    [source.value.seller, 'Vendedor(a)'],
    [source.value.manager, 'Gerente responsável'],
  ]
    .map(([person, role]) => {
      const name = firstValue(person, ['name', 'full_name']);
      return name
        ? {
            id: `${role}-${name}`,
            name,
            role,
            avatar: firstValue(person, [
              'avatar_url',
              'thumbnail_url',
              'contact.avatar_url',
              'contact.thumbnail_url',
            ]),
            phone: firstValue(person, [
              'phone_number',
              'phone',
              'contact.phone_number',
            ]),
            email: firstValue(person, ['email', 'contact.email']),
          }
        : null;
    })
    .filter(Boolean);
  const people = partyRecords
    .map((participant, index) => {
      const name = firstValue(participant, [
        'name',
        'contact.name',
        'company.name',
        'representative.name',
      ]);
      if (!name) return null;
      return {
        id: participant.id || `${name}-${index}`,
        name,
        role: firstValue(
          participant,
          ['party_role_label', 'party_role', 'role'],
          'Participante'
        ),
        avatar: firstValue(participant, [
          'avatar_url',
          'thumbnail_url',
          'contact.avatar_url',
          'contact.thumbnail_url',
        ]),
        phone: firstValue(participant, [
          'phone_number',
          'phone',
          'contact.phone_number',
        ]),
        email: firstValue(participant, ['email', 'contact.email']),
      };
    })
    .filter(Boolean);
  const result = [...people, ...fixedPeople];
  return result
    .filter(
      (person, index) =>
        result.findIndex(
          item => item.name === person.name && item.role === person.role
        ) === index
    )
    .slice(0, 6)
    .map(person => ({ ...person, initials: initials(person.name) }));
});
const metrics = computed(() => [
  [
    'Valor da venda',
    model.detail?.amount || money(source.value.contracted_total),
    'i-lucide-banknote',
  ],
  [
    'Referência',
    firstValue(
      source.value,
      ['reference'],
      model.detail?.id ? `#${model.detail.id}` : '—'
    ),
    'i-lucide-hash',
  ],
  ['Etapa', statusLabel.value, 'i-lucide-route'],
  ['Atualizada em', shortDate(source.value.updated_at), 'i-lucide-clock-3'],
]);
const detailFacts = computed(() => [
  ['Endereço', propertyAddress.value || 'Não informado'],
  ['Localização', propertyLocation.value || 'Não informado'],
  ['Gerente responsável', manager.value || 'Não informado'],
  [
    'Abertura',
    shortDate(firstValue(source.value, ['opened_at', 'created_at'])),
  ],
  [
    'Previsão de assinatura',
    shortDate(
      firstValue(source.value, [
        'expected_signature_at',
        'signature_due_at',
        'times.delivered_at',
      ])
    ),
  ],
  [
    'Pós-venda',
    firstValue(
      source.value,
      ['post_sale.status_label', 'post_sale.status'],
      'Não iniciado'
    ),
  ],
]);
const documents = computed(() =>
  asArray(
    source.value.documents ||
      source.value.property_documents ||
      source.value.document_types
  )
    .map((document, index) => ({
      id:
        document.id ||
        `${document.name || document.title || 'document'}-${index}`,
      title: firstValue(document, ['title', 'name', 'label'], 'Documento'),
      status: firstValue(document, ['status_label', 'status'], 'Pendente'),
      updatedAt: shortDate(firstValue(document, ['updated_at', 'created_at'])),
    }))
    .slice(0, 5)
);
const pendingItems = computed(() =>
  asArray(
    source.value.pending_items ||
      source.value.pendencies ||
      source.value.checklist_items
  )
    .filter(
      item => !['done', 'completed', 'concluido'].includes(String(item.status))
    )
    .map((item, index) => ({
      id: item.id || `${item.title || item.name || 'pending'}-${index}`,
      title: firstValue(
        item,
        ['title', 'name', 'label'],
        'Pendência operacional'
      ),
      status: firstValue(item, ['status_label', 'status'], 'Pendente'),
    }))
    .slice(0, 4)
);
const actionOptions = computed(() => model.actionOptions || []);
const saleListings = computed(
  () => realData.data.value?.createOptions?.listings || []
);
const selectedListingId = computed(() =>
  String(model.form.imovel_listing_id || '')
);

const listingTitle = listing =>
  firstValue(listing, [
    'title',
    'titulo',
    'imovel.title',
    'imovel.titulo',
    'address.title',
    'id',
  ]);
const listingAddress = listing =>
  firstValue(listing, [
    'endereco',
    'address.endereco',
    'imovel.endereco',
    'logradouro',
    'address.logradouro',
  ]);
const listingImage = listing =>
  firstValue(listing, [
    'cover_image_url',
    'image_url',
    'image',
    'imovel.cover_image_url',
    'imovel.image_url',
    'midias.0.url',
    'midias.0.file_url',
  ]);
const listingPrice = listing =>
  firstValue(listing, [
    'listing_price',
    'primary_listing.price_amount',
    'valor_venda',
    'value',
    'price',
    'imovel.valor_venda',
  ]);

const selectListing = listing => {
  model.form.imovel_listing_id = String(listing.id);
  if (!model.form.title) model.form.title = `Venda — ${listingTitle(listing)}`;
  if (!model.form.contracted_total && listingPrice(listing)) {
    model.form.contracted_total = String(listingPrice(listing));
  }
};

const runAction = async action => {
  await model.runAction(action);
};

const createSale = async () => {
  const created = await model.create();
  const id = recordIdFromResponse(created);
  if (id) emit('navigate', { id, screen: 'detail' });
};

const toDashboard = () => emit('navigate', { screen: 'dashboard' });
const toDetail = () =>
  emit('navigate', { id: model.detail?.id, screen: 'detail' });
const toEdit = () => emit('navigate', { id: model.detail?.id, screen: 'edit' });
</script>

<template>
  <main
    class="mx-auto w-full max-w-[1536px] pb-8 pl-[34px] pr-[25px] pt-3 text-[#111b55]"
  >
    <header
      class="flex flex-wrap items-start justify-between gap-4"
      data-imudei-visual-id="sales-detail-header"
    >
      <div>
        <p class="text-[10px] font-semibold text-[#5c35c7]">
          Vendas<span v-if="model.detail"> / {{ model.detail.id }}</span>
        </p>
        <div class="mt-1 flex flex-wrap items-center gap-3">
          <h1 class="text-[24px] font-bold tracking-[-0.03em]">
            {{
              isCreate
                ? 'Cadastrar venda'
                : isEdit
                  ? 'Editar venda'
                  : model.detail?.title || 'Venda'
            }}
          </h1>
          <span
            v-if="model.detail"
            class="rounded-full bg-violet-50 px-2.5 py-1 text-[9px] font-bold text-[#5a24c7]"
          >
            {{ statusLabel }}
          </span>
        </div>
        <p class="mt-1 text-[10px] text-[#66729a]">
          {{
            isCreate
              ? 'Selecione um anúncio real para iniciar a operação de venda.'
              : model.detail?.subtitle ||
                'Acompanhe os marcos e as validações da operação.'
          }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="grid size-9 place-items-center rounded-lg border border-[#e9e5f2] bg-white text-[#5420c4] disabled:opacity-50"
          type="button"
          aria-label="Atualizar dados"
          :disabled="loading || saving"
          @click="refresh"
        >
          <i
            class="i-lucide-refresh-cw size-4"
            :class="loading ? 'animate-spin' : ''"
          />
        </button>
        <button
          v-if="isDetail && model.detail"
          class="h-9 rounded-lg border border-violet-300 bg-white px-5 text-[10px] font-bold text-[#5420c4]"
          type="button"
          @click="toEdit"
        >
          <i class="i-lucide-pencil mr-2 inline-block size-3.5" />Editar venda
        </button>
      </div>
    </header>

    <div
      v-if="model.notice || model.localError || error"
      class="mt-3 flex items-center gap-2 rounded-xl border px-4 py-3 text-[10px]"
      :class="
        model.localError || error
          ? 'border-rose-100 bg-rose-50 text-rose-700'
          : 'border-emerald-100 bg-emerald-50 text-emerald-700'
      "
    >
      <i
        class="size-4"
        :class="
          model.localError || error
            ? 'i-lucide-circle-alert'
            : 'i-lucide-circle-check-big'
        "
      />
      {{ model.localError || error || model.notice }}
    </div>

    <template v-if="isCreate">
      <section class="mt-4 grid grid-cols-[minmax(0,1fr)_300px] gap-4">
        <div class="min-w-0 space-y-4">
          <article
            class="rounded-2xl border border-[#ece8f4] bg-white px-5 py-4 shadow-[0_6px_24px_rgba(36,22,90,0.05)]"
          >
            <div class="grid grid-cols-5 gap-3">
              <div
                v-for="(step, index) in [
                  'Imóvel',
                  'Valores',
                  'Documentos',
                  'Revisão',
                  'Concluir',
                ]"
                :key="step"
                class="flex min-w-0 items-center gap-2"
              >
                <span
                  class="grid size-9 shrink-0 place-items-center rounded-full text-[11px] font-bold"
                  :class="
                    index === 0
                      ? 'bg-gradient-to-br from-[#4e1ab8] to-[#ce2ecf] text-white shadow-[0_5px_12px_rgba(94,41,187,0.23)]'
                      : 'bg-[#f4f1f8] text-[#5a24c7]'
                  "
                >
                  {{ index + 1 }}
                </span>
                <span class="min-w-0">
                  <strong class="block truncate text-[9px]">{{ step }}</strong>
                  <small class="block truncate text-[8px] text-[#7b86a1]">
                    {{ index === 0 ? 'Selecionar anúncio' : 'Operação real' }}
                  </small>
                </span>
              </div>
            </div>
          </article>

          <form
            class="rounded-2xl border border-[#ece8f4] bg-white p-5"
            @submit.prevent="createSale"
          >
            <header class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="text-[14px] font-bold">Selecione o imóvel</h2>
                <p class="mt-1 text-[9px] text-[#69759c]">
                  A lista abaixo vem do catálogo real de imóveis e anúncios da
                  conta.
                </p>
              </div>
              <span
                class="rounded-full bg-violet-50 px-3 py-1.5 text-[8px] font-bold text-[#5a24c7]"
              >
                {{ saleListings.length }} anúncios disponíveis
              </span>
            </header>

            <div v-if="saleListings.length" class="mt-5 grid grid-cols-3 gap-3">
              <button
                v-for="listing in saleListings"
                :key="listing.id"
                class="overflow-hidden rounded-xl border text-left transition"
                :class="
                  selectedListingId === String(listing.id)
                    ? 'border-[#6d38cf] bg-[#fbf8ff] ring-1 ring-[#cbb5fb]'
                    : 'border-[#e7e1ef] bg-white hover:border-[#bca0ef]'
                "
                type="button"
                @click="selectListing(listing)"
              >
                <div
                  class="grid h-28 place-items-center bg-[#f2edff] text-[#5a24c7]"
                >
                  <img
                    v-if="listingImage(listing)"
                    class="h-full w-full object-cover"
                    :src="listingImage(listing)"
                    :alt="listingTitle(listing)"
                  />
                  <i v-else class="i-lucide-building-2 size-8" />
                </div>
                <span class="block p-3">
                  <strong class="block truncate text-[10px]">{{
                    listingTitle(listing)
                  }}</strong>
                  <small class="mt-1 block truncate text-[8px] text-[#7180a2]">
                    {{ listingAddress(listing) || 'Endereço não informado' }}
                  </small>
                  <b class="mt-2 block text-[10px] text-[#5a24c7]">
                    {{
                      listingPrice(listing)
                        ? money(listingPrice(listing))
                        : 'Valor não informado'
                    }}
                  </b>
                </span>
              </button>
            </div>
            <div
              v-else
              class="mt-5 grid min-h-52 place-items-center rounded-xl border border-dashed border-[#dcccfa] bg-[#fcfaff] px-8 text-center"
            >
              <span>
                <i class="i-lucide-building-x mx-auto size-7 text-[#8968d4]" />
                <strong class="mt-3 block text-[11px]"
                  >Nenhum anúncio elegível foi retornado</strong
                >
                <p class="mt-2 max-w-md text-[9px] leading-5 text-[#69759c]">
                  O cadastro permanece indisponível até que a API devolva um
                  anúncio para a operação de venda.
                </p>
              </span>
            </div>

            <section
              class="mt-5 grid grid-cols-2 gap-4 border-t border-[#eeeaf4] pt-5"
            >
              <label class="text-[9px] font-semibold">
                Título da venda
                <input
                  v-model="model.form.title"
                  class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] px-3 text-[10px] font-normal outline-none focus:border-[#805ce1]"
                  type="text"
                  placeholder="Opcional — o backend gera um título se vazio"
                />
              </label>
              <label class="text-[9px] font-semibold">
                Valor contratado
                <input
                  v-model="model.form.contracted_total"
                  class="mt-2 h-10 w-full rounded-xl border border-[#e3deea] px-3 text-[10px] font-normal outline-none focus:border-[#805ce1]"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Opcional — usa a oferta/listagem quando disponível"
                />
              </label>
            </section>

            <footer
              class="mt-6 flex items-center justify-between border-t border-[#eeeaf4] pt-5"
            >
              <button
                class="rounded-xl border border-[#e5e0ec] bg-white px-5 py-3 text-[10px] font-semibold text-[#4d5578]"
                type="button"
                @click="toDashboard"
              >
                Cancelar
              </button>
              <button
                class="rounded-xl bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-6 py-3 text-[10px] font-bold text-white shadow-[0_7px_18px_rgba(94,41,187,0.2)] disabled:cursor-not-allowed disabled:opacity-50"
                type="submit"
                :disabled="saving || !model.form.imovel_listing_id"
              >
                {{ saving ? 'Criando venda…' : 'Criar venda' }}
              </button>
            </footer>
          </form>
        </div>

        <aside class="space-y-4">
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <h2 class="text-[11px] font-bold">Resumo do cadastro</h2>
            <dl class="mt-4 divide-y divide-[#eeeaf4] text-[8px]">
              <div class="flex justify-between py-3">
                <dt class="text-[#7b86a1]">Anúncio selecionado</dt>
                <dd class="font-semibold text-[#5a24c7]">
                  {{ model.form.imovel_listing_id || 'Aguardando seleção' }}
                </dd>
              </div>
              <div class="flex justify-between py-3">
                <dt class="text-[#7b86a1]">Operação</dt>
                <dd class="font-semibold">Venda</dd>
              </div>
              <div class="flex justify-between py-3">
                <dt class="text-[#7b86a1]">Origem</dt>
                <dd class="font-semibold">API real</dd>
              </div>
            </dl>
          </article>
          <article
            class="rounded-2xl border border-[#eadff9] bg-gradient-to-br from-white to-[#fff2fc] p-4"
          >
            <div class="flex items-center gap-2">
              <span
                class="grid size-8 place-items-center rounded-xl bg-violet-100 text-[#5a24c7]"
              >
                <i class="i-lucide-shield-check size-4" />
              </span>
              <h2 class="text-[11px] font-bold">Cadastro protegido</h2>
            </div>
            <p class="mt-3 text-[9px] leading-5 text-[#69759c]">
              A validação de elegibilidade, oferta e permissões é feita no
              backend antes da criação da venda.
            </p>
          </article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <h2 class="text-[11px] font-bold">Jornada da venda</h2>
            <div class="mt-4 space-y-3">
              <div
                v-for="(stage, index) in salesStages"
                :key="stage[0]"
                class="flex gap-3"
              >
                <span
                  class="grid size-6 place-items-center rounded-full text-[8px] font-bold"
                  :class="
                    index === 0
                      ? 'bg-[#5a24c7] text-white'
                      : 'bg-[#f1eff4] text-[#9ba4bc]'
                  "
                >
                  {{ index + 1 }}
                </span>
                <span>
                  <b class="block text-[9px]">{{ stage[0] }}</b>
                  <small class="block text-[8px] text-[#7b86a1]">
                    {{ index === 0 ? 'Aguardando criação' : 'Próxima etapa' }}
                  </small>
                </span>
              </div>
            </div>
          </article>
        </aside>
      </section>
    </template>

    <template v-else-if="model.detail">
      <section
        data-imudei-visual-id="sales-detail-hero"
        class="mt-0 overflow-hidden rounded-2xl border border-[#e9e2f4] bg-white shadow-[0_6px_24px_rgba(36,22,90,0.04)]"
      >
        <div
          class="grid min-h-[202px] grid-cols-[320px_240px_minmax(0,1fr)_210px]"
        >
          <div class="p-2">
            <div
              class="grid h-[184px] place-items-center overflow-hidden rounded-xl bg-[#f0ebff] text-[#5a24c7]"
            >
              <img
                v-if="propertyImage"
                :src="propertyImage"
                :alt="model.detail.title"
                class="h-full w-full object-cover"
              />
              <span v-else class="px-6 text-center">
                <i class="i-lucide-building-2 mx-auto size-9" />
                <p class="mt-3 text-[8px] leading-4 text-[#6f5ca3]">
                  Imagem do imóvel não disponibilizada pela operação.
                </p>
              </span>
            </div>
          </div>
          <div class="border-l border-[#eeeaf4] px-4 py-3">
            <header class="flex items-center gap-2 text-[#2d2764]">
              <i class="i-lucide-users-round size-4 text-[#5a24c7]" />
              <h2 class="text-[9px] font-bold">Participantes principais</h2>
            </header>
            <div v-if="participants.length" class="mt-3 space-y-3">
              <div
                v-for="person in participants.slice(0, 2)"
                :key="person.id"
                class="flex items-center gap-2.5"
              >
                <span
                  class="grid size-10 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-violet-300 bg-violet-100 text-[8px] font-bold text-[#5a24c7]"
                >
                  <img
                    v-if="person.avatar"
                    :src="person.avatar"
                    :alt="person.name"
                    class="h-full w-full object-cover"
                  />
                  <template v-else>{{ person.initials }}</template>
                </span>
                <span class="min-w-0">
                  <small class="block truncate text-[8px] text-[#7c87a4]">{{
                    person.role
                  }}</small>
                  <strong class="block truncate text-[10px]">{{
                    person.name
                  }}</strong>
                  <small class="block truncate text-[8px] text-[#69759c]">{{
                    person.phone || person.email || 'Contato não informado'
                  }}</small>
                </span>
              </div>
            </div>
            <p v-else class="mt-7 text-[8px] leading-4 text-[#7b86a1]">
              Sem participantes retornados pela operação.
            </p>
          </div>
          <div class="border-l border-[#eeeaf4] px-4 py-3">
            <div class="grid grid-cols-4 divide-x divide-[#eeeaf4]">
              <div
                v-for="metric in metrics"
                :key="metric[0]"
                class="min-w-0 px-2.5 first:pl-0"
              >
                <span class="flex items-center gap-1 text-[8px] text-[#7b86a1]">
                  <i :class="metric[2]" class="size-3 text-[#5a24c7]" />
                  {{ metric[0] }}
                </span>
                <strong
                  class="mt-1.5 block truncate text-[10px] text-[#25106a]"
                  >{{ metric[1] }}</strong
                >
              </div>
            </div>
            <div
              class="mt-3 grid grid-cols-4 gap-2 rounded-xl bg-[#fbfaff] px-3 py-2.5"
            >
              <div
                v-for="fact in detailFacts.slice(0, 4)"
                :key="fact[0]"
                class="min-w-0"
              >
                <span class="block truncate text-[7px] text-[#7b86a1]">{{
                  fact[0]
                }}</span>
                <strong class="mt-1 block truncate text-[8px]">{{
                  fact[1]
                }}</strong>
              </div>
            </div>
            <div class="relative mt-4 grid grid-cols-4">
              <div
                v-for="(stage, index) in salesStages"
                :key="stage[0]"
                class="relative z-10 flex flex-col items-center text-center"
              >
                <span
                  class="grid size-6 place-items-center rounded-full text-[8px] font-bold"
                  :class="
                    index <= stageIndex
                      ? 'bg-[#5a24c7] text-white'
                      : 'border border-[#d8d7e4] bg-white text-[#97a1b8]'
                  "
                >
                  {{ index < stageIndex ? '✓' : index + 1 }}
                </span>
                <strong class="mt-1.5 text-[8px]">{{ stage[0] }}</strong>
                <small class="text-[7px] text-[#7b86a1]">
                  {{
                    index === stageIndex
                      ? 'Etapa atual'
                      : index < stageIndex
                        ? 'Concluída'
                        : 'Pendente'
                  }}
                </small>
                <span
                  v-if="index < salesStages.length - 1"
                  class="absolute left-[calc(50%+14px)] top-3 h-px w-[calc(100%-28px)] bg-[#ddd9e8]"
                  :class="index < stageIndex ? 'bg-[#5a24c7]' : ''"
                />
              </div>
            </div>
          </div>
          <aside
            class="border-l border-[#eeeaf4] bg-gradient-to-b from-white to-[#fff5fc] px-4 py-3 text-center"
          >
            <span
              class="text-[8px] font-bold uppercase tracking-[0.1em] text-[#7a55cd]"
              >Recompensas</span
            >
            <span
              class="mx-auto mt-4 grid size-12 place-items-center rounded-full border-4 border-[#dcd0ff] text-[#8a5ede]"
            >
              <i class="i-lucide-gift size-5" />
            </span>
            <p class="mt-4 text-[8px] leading-4 text-[#7380a0]">
              Pontuação indisponível sem contrato de gamificação no backend.
            </p>
          </aside>
        </div>
      </section>

      <section
        class="mt-2.5 overflow-hidden rounded-2xl border border-[#ece8f4] bg-white shadow-[0_5px_18px_rgba(36,22,90,0.035)]"
      >
        <div
          class="grid min-h-[64px] grid-cols-[56px_minmax(0,1.2fr)_minmax(220px,0.8fr)_170px] items-center gap-4 px-5"
        >
          <span
            class="grid size-10 place-items-center rounded-full bg-violet-50 text-[#6a2ed2]"
          >
            <i class="i-lucide-target size-5" />
          </span>
          <div class="min-w-0 border-r border-[#eeeaf4] pr-4">
            <span
              class="text-[8px] font-bold uppercase tracking-[0.1em] text-[#6a2ed2]"
              >Missão da venda</span
            >
            <strong class="mt-1 block text-[11px]">
              Acompanhar a operação
            </strong>
            <p class="mt-0.5 truncate text-[8px] text-[#7380a0]">
              Metas e pontos não foram retornados pelo backend desta venda.
            </p>
          </div>
          <div class="min-w-0">
            <div class="flex items-center justify-between gap-3 text-[8px]">
              <span class="font-semibold">Progresso operacional</span>
              <span class="shrink-0 text-[#74809d]"
                >{{ stageIndex + 1 }} de {{ salesStages.length }} marcos</span
              >
            </div>
            <div class="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#e9e5f0]">
              <span
                class="block h-full rounded-full bg-[#5a24c7]"
                :class="
                  stageIndex === 0
                    ? 'w-1/4'
                    : stageIndex === 1
                      ? 'w-2/4'
                      : stageIndex === 2
                        ? 'w-3/4'
                        : 'w-full'
                "
              />
            </div>
            <p class="mt-0.5 text-[7px] text-[#74809d]">
              O avanço usa a etapa real retornada pela operação.
            </p>
          </div>
          <span
            class="justify-self-end rounded-full border border-[#dfd3fc] px-3 py-2 text-center text-[8px] font-semibold text-[#7650be]"
          >
            Recompensas indisponíveis
          </span>
        </div>
      </section>

      <section
        v-if="isDetail"
        data-imudei-visual-id="sales-detail-content"
        class="mt-3 grid grid-cols-[minmax(0,1fr)_484px] gap-4"
      >
        <div class="space-y-2">
          <nav
            data-imudei-visual-id="sales-detail-tabs"
            class="flex h-9 items-center gap-7 rounded-xl border border-[#ece8f4] bg-white px-5 text-[8px] font-semibold text-[#6b779a]"
          >
            <span class="border-b-2 border-[#5a24c7] py-[10px] text-[#5a24c7]"
              >Visão geral</span
            >
            <span>Participantes</span>
            <span>Documentos</span>
            <span>Financeiro</span>
            <span>Histórico</span>
            <span class="inline-flex items-center gap-1">
              Gamificação
              <small
                class="rounded-full bg-violet-50 px-1.5 py-0.5 text-[7px] text-[#7650be]"
                >Indisponível</small
              >
            </span>
          </nav>
          <div class="grid grid-cols-[minmax(0,1.04fr)_minmax(0,0.96fr)] gap-4">
            <div class="flex flex-col gap-4">
              <article
                class="order-2 min-h-[160px] rounded-2xl border border-[#ece8f4] bg-white p-4"
              >
                <header class="flex items-center justify-between">
                  <div>
                    <h2 class="text-[12px] font-bold">Dados da negociação</h2>
                    <p class="mt-1 text-[8px] text-[#7b86a1]">
                      Informações confirmadas pela operação.
                    </p>
                  </div>
                  <span
                    class="rounded-full bg-violet-50 px-2 py-1 text-[8px] font-bold text-[#5a24c7]"
                    >API real</span
                  >
                </header>
                <dl class="mt-4 grid grid-cols-3 gap-x-5 gap-y-4 text-[8px]">
                  <div
                    v-for="fact in detailFacts"
                    :key="fact[0]"
                    class="min-w-0"
                  >
                    <dt class="truncate text-[#7c87a4]">{{ fact[0] }}</dt>
                    <dd class="mt-1 truncate font-semibold">{{ fact[1] }}</dd>
                  </div>
                </dl>
              </article>
              <article
                class="order-1 min-h-[166px] rounded-2xl border border-[#ece8f4] bg-white p-4"
              >
                <header class="flex items-center justify-between">
                  <div>
                    <h2 class="text-[12px] font-bold">Participantes</h2>
                    <p class="mt-1 text-[8px] text-[#7b86a1]">
                      Pessoas vinculadas à venda.
                    </p>
                  </div>
                  <span class="text-[8px] text-[#5a24c7]"
                    >{{ participants.length }} retornados</span
                  >
                </header>
                <div
                  v-if="participants.length"
                  class="mt-3 divide-y divide-[#eeeaf4]"
                >
                  <div
                    v-for="person in participants"
                    :key="person.id"
                    class="flex min-h-10 items-center gap-3 py-1.5"
                  >
                    <span
                      class="grid size-7 place-items-center overflow-hidden rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]"
                    >
                      <img
                        v-if="person.avatar"
                        :src="person.avatar"
                        :alt="person.name"
                        class="h-full w-full object-cover"
                      />
                      <template v-else>{{ person.initials }}</template>
                    </span>
                    <span class="min-w-0">
                      <strong class="block truncate text-[9px]">{{
                        person.name
                      }}</strong>
                      <small class="block truncate text-[7px] text-[#7c87a4]">{{
                        person.role
                      }}</small>
                    </span>
                  </div>
                </div>
                <p
                  v-else
                  class="mt-4 rounded-xl border border-dashed border-[#e6dffc] p-4 text-[9px] text-[#69759c]"
                >
                  A API ainda não retornou participantes para esta venda.
                </p>
              </article>
              <article
                v-if="documents.length"
                class="order-3 rounded-2xl border border-[#ece8f4] bg-white p-4"
              >
                <header class="flex items-center justify-between">
                  <div>
                    <h2 class="text-[12px] font-bold">
                      Documentos e validações
                    </h2>
                    <p class="mt-1 text-[8px] text-[#7b86a1]">
                      Itens relacionados recebidos da operação.
                    </p>
                  </div>
                  <i class="i-lucide-files size-4 text-[#5a24c7]" />
                </header>
                <div
                  v-if="documents.length"
                  class="mt-4 divide-y divide-[#eeeaf4]"
                >
                  <div
                    v-for="document in documents"
                    :key="document.id"
                    class="flex items-center justify-between gap-4 py-3"
                  >
                    <span class="flex min-w-0 items-center gap-3">
                      <i class="i-lucide-file-check-2 size-4 text-[#5a24c7]" />
                      <span class="min-w-0"
                        ><strong class="block truncate text-[9px]">{{
                          document.title
                        }}</strong
                        ><small
                          class="block truncate text-[8px] text-[#7b86a1]"
                          >{{ document.updatedAt }}</small
                        ></span
                      >
                    </span>
                    <span
                      class="rounded-full bg-violet-50 px-2 py-1 text-[8px] font-semibold text-[#5a24c7]"
                      >{{ document.status }}</span
                    >
                  </div>
                </div>
                <p
                  v-else
                  class="mt-4 rounded-xl bg-[#fbfaff] p-4 text-[9px] text-[#69759c]"
                >
                  Nenhum documento foi devolvido no contrato atual desta venda.
                </p>
              </article>
            </div>
            <div class="space-y-4">
              <article
                class="min-h-[336px] rounded-2xl border border-[#ece8f4] bg-white p-4"
              >
                <header class="flex items-center justify-between gap-3">
                  <div>
                    <h2 class="text-[11px] font-bold">Marcos da venda</h2>
                    <p class="mt-1 text-[8px] text-[#7c87a4]">
                      Etapas calculadas pelo status atual da operação.
                    </p>
                  </div>
                  <i class="i-lucide-route size-4 shrink-0 text-[#5a24c7]" />
                </header>
                <div
                  class="relative mt-5 space-y-4 before:absolute before:bottom-4 before:left-[13px] before:top-4 before:w-px before:bg-[#e4deed]"
                >
                  <div
                    v-for="(stage, index) in salesStages"
                    :key="stage[0]"
                    class="relative z-10 flex gap-3"
                  >
                    <span
                      class="grid size-7 place-items-center rounded-full text-[8px] font-bold"
                      :class="
                        index <= stageIndex
                          ? 'bg-[#e9e0ff] text-[#5a24c7]'
                          : 'bg-[#f1eff4] text-[#9ba4bc]'
                      "
                      >{{ index < stageIndex ? '✓' : index + 1 }}</span
                    >
                    <span class="min-w-0 flex-1">
                      <span class="flex items-center justify-between gap-2">
                        <strong class="block text-[9px]">{{ stage[0] }}</strong>
                        <small class="shrink-0 text-[7px] text-[#8a76ac]"
                          >Recompensa indisponível</small
                        >
                      </span>
                      <small class="mt-1 block text-[8px] text-[#7c87a4]">{{
                        index === stageIndex
                          ? 'Etapa atual'
                          : index < stageIndex
                            ? 'Concluída'
                            : 'Pendente'
                      }}</small>
                    </span>
                  </div>
                </div>
                <div class="mt-5 rounded-xl bg-[#fbfaff] p-3">
                  <h3 class="text-[9px] font-bold">Próximo marco</h3>
                  <p
                    v-if="stageIndex < salesStages.length - 1"
                    class="mt-2 text-[8px] leading-4 text-[#69759c]"
                  >
                    {{
                      salesStages[
                        Math.min(stageIndex + 1, salesStages.length - 1)
                      ][0]
                    }}
                    é acompanhado pela operação.
                  </p>
                  <p v-else class="mt-2 text-[8px] leading-4 text-[#69759c]">
                    Nenhum próximo marco foi retornado após a etapa atual.
                  </p>
                </div>
              </article>
              <article
                v-if="pendingItems.length"
                class="rounded-2xl border border-[#ece8f4] bg-white p-4"
              >
                <header class="flex items-center justify-between">
                  <h2 class="text-[11px] font-bold">Pendências</h2>
                  <i class="i-lucide-list-checks size-4 text-[#5a24c7]" />
                </header>
                <div v-if="pendingItems.length" class="mt-4 space-y-3">
                  <div
                    v-for="item in pendingItems"
                    :key="item.id"
                    class="rounded-xl bg-[#fffaf3] p-3"
                  >
                    <strong class="block text-[9px]">{{ item.title }}</strong
                    ><small class="mt-1 block text-[8px] text-[#967246]">{{
                      item.status
                    }}</small>
                  </div>
                </div>
                <p
                  v-else
                  class="mt-4 rounded-xl border border-dashed border-[#eeeaf4] p-3 text-[8px] leading-4 text-[#69759c]"
                >
                  O backend não retornou pendências abertas para esta venda.
                </p>
              </article>
            </div>
          </div>
        </div>
        <aside
          data-imudei-visual-id="sales-detail-sidebar"
          class="rounded-2xl border border-[#ece8f4] bg-white p-4"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[12px] font-bold">Atividades</h2>
            <i class="i-lucide-filter size-4 text-[#5a24c7]" />
          </header>
          <div v-if="model.history.length" class="mt-4 space-y-3">
            <div
              v-for="event in model.history.slice(0, 6)"
              :key="event.id"
              class="flex gap-3"
            >
              <span
                class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"
                ><i class="i-lucide-activity size-3" /></span
              ><span class="min-w-0"
                ><strong class="block truncate text-[9px]">{{
                  event.label
                }}</strong
                ><small class="block truncate text-[8px] text-[#7c87a4]"
                  >{{ event.actor }} · {{ event.date }}</small
                ></span
              >
            </div>
          </div>
          <div v-else class="grid min-h-[258px] place-items-center text-center">
            <span
              ><i class="i-lucide-history mx-auto size-7 text-[#ab90dc]" />
              <p class="mt-3 max-w-[180px] text-[9px] leading-5 text-[#69759c]">
                O histórico real aparece quando a API devolve eventos desta
                venda.
              </p></span
            >
          </div>
        </aside>
      </section>

      <footer
        v-if="isDetail"
        data-imudei-visual-id="sales-detail-footer"
        class="mt-3 grid min-h-[62px] grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-5 rounded-2xl border border-[#e3d9fa] bg-gradient-to-r from-white via-[#fdfaff] to-[#f5efff] px-5 py-3"
      >
        <div class="flex min-w-0 items-center gap-3">
          <span
            class="grid size-10 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"
          >
            <i class="i-lucide-trophy size-5" />
          </span>
          <span class="min-w-0">
            <strong class="block text-[10px]">Progresso da operação</strong>
            <small class="mt-1 block text-[8px] leading-4 text-[#69759c]">
              As recompensas sociais permanecem indisponíveis até a API fornecer
              esse contrato para a venda.
            </small>
          </span>
        </div>
        <div class="flex items-center gap-1.5" aria-label="Marcos da venda">
          <span
            v-for="(stage, index) in salesStages"
            :key="`footer-${stage[0]}`"
            class="size-3 rounded-full border-2 border-white"
            :class="index <= stageIndex ? 'bg-[#5a24c7]' : 'bg-[#d9d4e3]'"
            :title="stage[0]"
          />
        </div>
        <div class="flex items-center justify-end gap-2">
          <button
            v-for="action in actionOptions.slice(0, 2)"
            :key="`footer-${action.name}`"
            class="rounded-lg border border-[#d8c7fa] bg-white px-3 py-2 text-[8px] font-semibold text-[#5a24c7] disabled:opacity-50"
            type="button"
            :disabled="saving"
            @click="runAction(action.name)"
          >
            {{ action.label }}
          </button>
          <button
            class="rounded-lg bg-[#5a24c7] px-3 py-2 text-[8px] font-bold text-white"
            type="button"
            @click="toEdit"
          >
            Editar venda
          </button>
        </div>
      </footer>

      <section
        v-else-if="isEdit"
        class="mt-4 grid grid-cols-[230px_minmax(0,1fr)_250px] gap-4"
      >
        <aside class="space-y-4">
          <article class="rounded-2xl border border-[#ece8f4] bg-white p-4">
            <h2 class="text-[11px] font-bold">Jornada da venda</h2>
            <div class="mt-4 space-y-4">
              <div
                v-for="(stage, index) in salesStages"
                :key="stage[0]"
                class="flex gap-3"
              >
                <span
                  class="grid size-7 place-items-center rounded-full text-[8px] font-bold"
                  :class="
                    index <= stageIndex
                      ? 'bg-[#e9e0ff] text-[#5a24c7]'
                      : 'bg-[#f1eff4] text-[#9ba4bc]'
                  "
                  >{{ index < stageIndex ? '✓' : index + 1 }}</span
                ><span
                  ><strong class="block text-[9px]">{{ stage[0] }}</strong
                  ><small class="block text-[8px] text-[#7c87a4]">{{
                    index === stageIndex ? 'Etapa atual' : 'Fluxo operacional'
                  }}</small></span
                >
              </div>
            </div>
          </article>
          <article
            class="rounded-2xl border border-[#eadff9] bg-gradient-to-br from-white to-[#fff2fc] p-4"
          >
            <h2 class="text-[11px] font-bold">Recompensas</h2>
            <p class="mt-3 text-[9px] leading-5 text-[#69759c]">
              Nenhuma pontuação é exibida sem retorno real da API.
            </p>
          </article>
        </aside>
        <form
          class="rounded-2xl border border-[#ece8f4] bg-white p-5"
          @submit.prevent="toDetail"
        >
          <header>
            <h2 class="text-[14px] font-bold">Atualização operacional</h2>
            <p class="mt-1 text-[9px] text-[#69759c]">
              As transições aparecem apenas quando liberadas pelo status e pela
              permissão desta venda.
            </p>
          </header>
          <label
            class="mt-5 block rounded-2xl border border-[#eeeaf4] bg-[#fcfbff] p-4 text-[10px] font-bold"
            >Motivo ou observação<textarea
              v-model="model.form.reason"
              class="mt-3 min-h-28 w-full rounded-xl border border-[#f0ecf6] bg-white px-3 py-3 text-[10px] font-normal outline-none focus:border-[#805ce1]"
              placeholder="Descreva somente a justificativa necessária para a ação."
            />
          </label>
          <div v-if="actionOptions.length" class="mt-4 flex flex-wrap gap-2">
            <button
              v-for="action in actionOptions"
              :key="action.name"
              class="rounded-lg border border-[#e6dffc] bg-white px-4 py-2.5 text-[9px] font-bold text-[#5a24c7] disabled:opacity-50"
              type="button"
              :disabled="saving"
              @click="runAction(action.name)"
            >
              {{ action.label }}
            </button>
          </div>
          <p
            v-else
            class="mt-4 rounded-xl bg-[#fbfaff] p-4 text-[9px] text-[#69759c]"
          >
            Nenhuma atualização direta foi autorizada pela API para a etapa
            atual.
          </p>
          <footer
            class="mt-6 flex items-center justify-between border-t border-[#eeeaf4] pt-5"
          >
            <button
              class="rounded-xl border border-[#e5e0ec] bg-white px-5 py-3 text-[10px] font-semibold text-[#4d5578]"
              type="button"
              @click="toDetail"
            >
              Cancelar</button
            ><span class="text-[8px] text-[#7b86a1]"
              >As ações recarregam o registro após a resposta da API.</span
            >
          </footer>
        </form>
        <aside class="space-y-4">
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <h2 class="text-[11px] font-bold">Resumo das alterações</h2>
            <dl class="mt-4 divide-y divide-[#eeeaf4] text-[8px]">
              <div
                v-for="fact in detailFacts.slice(0, 4)"
                :key="fact[0]"
                class="flex justify-between gap-3 py-3"
              >
                <dt class="text-[#7b86a1]">{{ fact[0] }}</dt>
                <dd class="truncate text-right font-semibold">{{ fact[1] }}</dd>
              </div>
            </dl>
          </article>
          <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
            <h2 class="text-[11px] font-bold">Atividade recente</h2>
            <div v-if="model.history.length" class="mt-4 space-y-3">
              <div
                v-for="event in model.history.slice(0, 5)"
                :key="event.id"
                class="flex gap-2"
              >
                <i
                  class="grid size-6 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"
                  ><i class="i-lucide-activity size-3" /></i
                ><span class="min-w-0"
                  ><strong class="block truncate text-[8px]">{{
                    event.label
                  }}</strong
                  ><small class="block truncate text-[7px] text-[#7b86a1]"
                    >{{ event.actor }} · {{ event.date }}</small
                  ></span
                >
              </div>
            </div>
            <p v-else class="mt-4 text-[8px] text-[#7b86a1]">
              Sem histórico retornado.
            </p>
          </article>
        </aside>
      </section>
    </template>

    <section
      v-else
      class="mt-4 grid min-h-[560px] place-items-center rounded-3xl border border-[#ece8f4] bg-white p-8 text-center"
    >
      <div class="max-w-md">
        <span
          class="mx-auto grid size-14 place-items-center rounded-2xl bg-[#f1ebff] text-[#5a24c7]"
          ><i class="i-lucide-file-search size-7"
        /></span>
        <h2 class="mt-5 text-[18px] font-bold">Venda não encontrada</h2>
        <p class="mt-2 text-[10px] leading-5 text-[#69759c]">
          O registro não foi devolvido pela API. A referência visual não
          substitui dados reais.
        </p>
        <button
          class="mt-5 rounded-xl bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-5 py-3 text-[10px] font-bold text-white"
          type="button"
          @click="toDashboard"
        >
          Voltar ao painel
        </button>
      </div>
    </section>
  </main>
</template>
